/**
 * PageLinkPicker — dropdown component that shows all pages
 * (including hidden ones) for easy link/path selection.
 *
 * Used in:
 * - CTAButtonBlock link field (ContentBlocks.tsx)
 * - Any future element that needs a page link
 */

import { useState, useRef, useEffect } from "react";
import { FileText, EyeOff, ChevronDown, ExternalLink, Search } from "lucide-react";
import { useCustomNav } from "./useCustomNav";

const btnReset = "appearance-none border-none font-inherit cursor-pointer p-0 m-0 leading-normal";

interface PageLinkPickerProps {
  value: string;
  onChange: (path: string) => void;
  /** Compact = smaller trigger, used inline in block editors */
  compact?: boolean;
  placeholder?: string;
}

export function PageLinkPicker({ value, onChange, compact, placeholder }: PageLinkPickerProps) {
  const { mergedNav, hiddenPaths } = useCustomNav();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    setTimeout(() => searchRef.current?.focus(), 80);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Build full page list: all mergedNav items + their children
  const allPages: { label: string; path: string; isHidden: boolean; isChild: boolean }[] = [];
  for (const item of mergedNav) {
    allPages.push({
      label: item.label,
      path: item.path,
      isHidden: !!item.isHidden || hiddenPaths.includes(item.path),
      isChild: false,
    });
    if (item.children) {
      for (const child of item.children) {
        allPages.push({
          label: child.label,
          path: child.path,
          isHidden: false,
          isChild: true,
        });
      }
    }
  }

  const filtered = search.trim()
    ? allPages.filter(
        (p) =>
          p.label.includes(search) ||
          p.path.toLowerCase().includes(search.toLowerCase())
      )
    : allPages;

  return (
    <div className="relative inline-block" ref={menuRef}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`${btnReset} flex items-center gap-1 rounded-md border border-gray-200 hover:bg-gray-50`}
        style={{
          padding: compact ? "2px 6px" : "4px 8px",
          fontSize: compact ? "10px" : "11px",
          color: "#6b7280",
          fontWeight: 500,
        }}
        title="בחירת עמוד"
      >
        <FileText className="shrink-0" style={{ width: compact ? 10 : 12, height: compact ? 10 : 12 }} />
        <span className="truncate max-w-[80px]">{value || placeholder || "בחר עמוד"}</span>
        <ChevronDown className="shrink-0" style={{ width: 10, height: 10 }} />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute z-50 top-full mt-1 bg-white rounded-xl shadow-2xl border border-gray-200 w-64 max-h-72 flex flex-col overflow-hidden"
          dir="rtl"
          style={{ left: 0 }}
        >
          {/* Search */}
          <div className="px-3 py-2 border-b border-gray-100">
            <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-2 py-1.5">
              <Search className="w-3 h-3 text-gray-400 shrink-0" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="חיפוש עמוד..."
                className="flex-1 bg-transparent outline-none text-xs text-gray-700"
              />
            </div>
          </div>

          {/* Pages list */}
          <div className="flex-1 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <p className="text-center text-xs text-gray-400 py-4">
                לא נמצאו עמודים
              </p>
            )}

            {/* External link option */}
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className={`${btnReset} flex items-center gap-2 w-full px-3 py-2 text-xs text-right hover:bg-gray-50`}
              style={{ color: "#9ca3af", fontWeight: 500 }}
            >
              <ExternalLink className="w-3 h-3 shrink-0" />
              <span>הזנה ידנית (URL חיצוני)</span>
            </button>

            <div className="h-px bg-gray-100 mx-2 my-1" />

            {filtered.map((page) => {
              const isActive = value === page.path;
              return (
                <button
                  key={page.path}
                  type="button"
                  onClick={() => {
                    onChange(page.path);
                    setOpen(false);
                    setSearch("");
                  }}
                  className={`${btnReset} flex items-center gap-2 w-full px-3 py-2 text-xs text-right ${
                    isActive ? "bg-blue-50" : "hover:bg-gray-50"
                  }`}
                  style={{
                    paddingRight: page.isChild ? "2rem" : "0.75rem",
                  }}
                >
                  {page.isHidden ? (
                    <EyeOff className="w-3 h-3 shrink-0" style={{ color: "#d97706" }} />
                  ) : (
                    <FileText className="w-3 h-3 shrink-0" style={{ color: isActive ? "#3b82f6" : "#9ca3af" }} />
                  )}
                  <span
                    className="flex-1 truncate"
                    style={{
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? "#3b82f6" : page.isHidden ? "#92400e" : "#374151",
                    }}
                  >
                    {page.label}
                  </span>
                  <span className="text-[10px] text-gray-300 shrink-0" dir="ltr">
                    {page.path}
                  </span>
                  {page.isHidden && (
                    <span
                      className="text-[9px] px-1 py-0.5 rounded shrink-0"
                      style={{ background: "#fef3c7", color: "#92400e", fontWeight: 600 }}
                    >
                      מוסתר
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}